import React, { useContext, useState } from 'react';
import { useEffect } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import MultiSelectEntity from '../RefineSearch/MultiSelectEntity';
import Popup from '../Common/Popup';

/**
 * The `CapitalSourceFilter` function is a React component that renders a filter for selecting capital
 * source options, and updates the selected options based on user input.
 * @returns a JSX element.
 */
function CapitalSourceFilter({ CapitalSourceOptions, setCapitalSourceOptions, customPathname, label = "Capital Source", isPopupOpen, setIsPopupOpen }) {

    const { CapitalSource, refineSearchFilters } = useContext(PeFilterContext);
    
    // Use internal state only if props are not provided (for backward compatibility)
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    useEffect(() => {
        const industryIds = refineSearchFilters[customPathname]?.capitalSource?.map(value => value.id) ?? [];
        setCapitalSourceOptions(CapitalSource?.map(industry => ({ ...industry, isChecked: industryIds.includes(parseInt(industry.id)) })))
    }, [CapitalSource, customPathname, refineSearchFilters, setCapitalSourceOptions])

    /* The `handleCapitalSourceChange` function is a callback function that is triggered when the user
    selects or deselects a capital source option. It receives an event object `e` as a parameter. */
    const handleCapitalSourceChange = ((e) => {
        const { name, checked, id, filteredIds } = e.target;

        if (name === "allSelect") {
            let currentCapitalSource = CapitalSourceOptions.map((capital) =>
                ({ ...capital, isChecked: checked })
            );
            setCapitalSourceOptions(currentCapitalSource);
        } else if (name === "filteredSelect" && filteredIds) {
            let currentCapitalSource = CapitalSourceOptions.map((capital) => {
                if (filteredIds.includes(capital.id)) {
                    return { ...capital, isChecked: checked };
                }
                return capital;
            });
            setCapitalSourceOptions(currentCapitalSource);
        } else {
            let currentCapitalSource = CapitalSourceOptions.map((capital) =>
                capital.id === parseInt(id) ? { ...capital, isChecked: checked } : capital
            );
            setCapitalSourceOptions(currentCapitalSource);
        }
    });

    /* The line `const len = CapitalSourceOptions?.filter(option => option.isChecked)?.length ?? 0` is
    calculating the length of the filtered `CapitalSourceOptions` array. */
    const selectedLength = CapitalSourceOptions?.filter(option => option.isChecked)?.length ?? 0;

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2">{label} <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. {CapitalSource.slice(0,2).map(item => item.name).join(', ')}</span></span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {selectedLength > 0 && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {selectedLength}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)}>
                    <div className="p-2">
                        <MultiSelectEntity
                            handleEntityChange={handleCapitalSourceChange}
                            primaryEntity={CapitalSourceOptions}
                        />
                    </div>
                </Popup>
            )}
        </div>
    );
}

export default CapitalSourceFilter;